import { api } from '@/lib/axios';
import type { Page } from '@/services/chantiers';

export type EnginType = 'PROPRE' | 'LOUE';
export type EnginStatut = 'DISPONIBLE' | 'AFFECTE' | 'MAINTENANCE' | 'HORS_SERVICE';
export type MaintenanceType = 'PREVENTIVE' | 'CORRECTIVE';
export type MaintenanceStatut = 'PLANIFIEE' | 'EN_COURS' | 'TERMINEE';
export type EnginCategorie = 'ENGIN_TP' | 'CAMION' | 'VEHICULE_LEGER' | 'UTILITAIRE' | 'BUS' | 'REMORQUE' | 'GROUPE_ELECTROGENE' | 'AUTRE';
export type EcheanceType = 'ASSURANCE' | 'CONTROLE_TECHNIQUE' | 'VIGNETTE' | 'CARTE_GRISE' | 'PERMIS_CIRCULATION' | 'AUTRE';

export const ENGIN_STATUTS: EnginStatut[] = ['DISPONIBLE', 'AFFECTE', 'MAINTENANCE', 'HORS_SERVICE'];
export const ENGIN_CATEGORIES: EnginCategorie[] = ['ENGIN_TP', 'CAMION', 'VEHICULE_LEGER', 'UTILITAIRE', 'BUS', 'REMORQUE', 'GROUPE_ELECTROGENE', 'AUTRE'];
export const CATEGORIE_LABEL: Record<EnginCategorie, string> = {
  ENGIN_TP: 'Engin TP', CAMION: 'Camion', VEHICULE_LEGER: 'Véhicule léger', UTILITAIRE: 'Utilitaire',
  BUS: 'Bus', REMORQUE: 'Remorque', GROUPE_ELECTROGENE: 'Groupe électrogène', AUTRE: 'Autre',
};
export const ECHEANCE_TYPES: EcheanceType[] = ['ASSURANCE', 'CONTROLE_TECHNIQUE', 'VIGNETTE', 'CARTE_GRISE', 'PERMIS_CIRCULATION', 'AUTRE'];
export const ECHEANCE_LABEL: Record<EcheanceType, string> = {
  ASSURANCE: 'Assurance', CONTROLE_TECHNIQUE: 'Contrôle technique', VIGNETTE: 'Vignette',
  CARTE_GRISE: 'Carte grise', PERMIS_CIRCULATION: 'Permis de circulation', AUTRE: 'Autre',
};

export interface EnginDTO {
  id: string;
  code: string;
  designation: string;
  categorie: EnginCategorie;
  marque: string | null;
  modele: string | null;
  immatriculation: string | null;
  annee_fabrication: number | null;
  date_acquisition: string | null;
  valeur_acquisition: number | null;
  duree_amortissement_ans: number | null;
  type: EnginType;
  statut: EnginStatut;
  compteur_actuel: number;
  unite_compteur: string;
  conso_reference_lh: number | null;
  prochaine_maintenance_date: string | null;
  prochaine_maintenance_km: number | null;
  chantier_actuel_id: string | null;
  loueur_id: string | null;
  cout_location_jour: number | null;
  location_date_debut: string | null;
  location_date_fin: string | null;
  is_archived: boolean;
  est_immobilise?: boolean;   // renseigné sur la fiche détail (S7)
}

export interface EnginInput {
  code: string;
  designation: string;
  categorie?: EnginCategorie;
  marque?: string | null;
  modele?: string | null;
  immatriculation?: string | null;
  annee_fabrication?: number | null;
  date_acquisition?: string | null;
  valeur_acquisition?: number | null;
  duree_amortissement_ans?: number | null;
  type: EnginType;
  statut: EnginStatut;
  compteur_actuel?: number;
  unite_compteur?: string;
  conso_reference_lh?: number | null;
  prochaine_maintenance_date?: string | null;
  prochaine_maintenance_km?: number | null;
  chantier_actuel_id?: string | null;
  loueur_id?: string | null;
  cout_location_jour?: number | null;
  location_date_debut?: string | null;
  location_date_fin?: string | null;
}

export interface EnginsDashboardDTO {
  total: number;
  disponibles: number;
  affectes: number;
  en_maintenance: number;
  hors_service: number;
  taux_disponibilite: number;
  maintenances_proches: number;
  echeances_proches: number;
  carburant_mois: number;
}

export interface EcheanceDTO {
  id: string;
  engin_id: string;
  type: EcheanceType;
  organisme: string | null;
  reference: string | null;
  date_debut: string | null;
  date_fin: string | null;
  montant: number | null;
  document_url: string | null;
  notes: string | null;
}

export interface EcheanceAvenirDTO extends EcheanceDTO {
  engin_code: string;
  engin_designation: string;
  jours_restants: number | null;
}

export interface ReleveDTO {
  id: string;
  engin_id: string;
  chantier_id: string | null;
  date: string;
  heures_marche: number;
  heures_arret: number;
  carburant_litre: number;
  prix_unitaire: number;
  carburant_cout: number;
  notes: string | null;
}

export interface MaintenanceDTO {
  id: string;
  engin_id: string;
  type: MaintenanceType;
  date_debut: string | null;
  date_fin: string | null;
  description: string | null;
  cout_pieces: number;
  cout_main_oeuvre: number;
  statut: MaintenanceStatut;
  compteur: number | null;
  plan_id: string | null;
  justificatif_url: string | null;
}

/* ----------------- Plans de maintenance préventive (heures/date) ---------- */
export type PlanDeclencheur = 'HEURES' | 'DATE' | 'LES_DEUX';
export type PlanStatut = 'EN_ATTENTE' | 'A_JOUR' | 'PROCHE' | 'DEPASSE';
export interface PlanDTO {
  id: string;
  engin_id: string;
  libelle: string;
  declencheur: PlanDeclencheur;
  periodicite_heures: number | null;
  periodicite_mois: number | null;
  ref_compteur: number | null;
  ref_date: string | null;
  actif: boolean;
  // Calculés :
  seuil_heures: number | null;
  reste_heures: number | null;
  seuil_date: string | null;
  reste_jours: number | null;
  statut: PlanStatut;
  compteur_actuel: number;
  unite_compteur: string;
}
export interface PlanInput {
  libelle: string;
  declencheur: PlanDeclencheur;
  periodicite_heures?: number | null;
  periodicite_mois?: number | null;
  ref_compteur?: number | null;
  ref_date?: string | null;
  actif?: boolean;
}
export interface PlanAlerteDTO extends PlanDTO {
  engin_code: string;
  engin_designation: string;
}

export interface AffectationDTO {
  id: string;
  engin_id: string;
  chantier_id: string;
  date_debut: string | null;
  date_fin: string | null;
  heures_planifiees: number | null;
}

export interface SyntheseEnginDTO {
  heures_marche: number;
  heures_arret: number;
  taux_utilisation: number | null;       // null = non calculable (« — »)
  carburant_litres: number;
  carburant_cout: number;
  consommation_lh: number | null;         // conso moyenne (depuis les pleins) ; null si non calculable
  consommation_unite: string | null;      // "L/100km" | "L/h"
  conso_reference_lh: number | null;      // référence paramétrée de l'engin
  surconsommation: boolean;
  nb_maintenances: number;
  cout_maintenance: number;
  cout_location: number;                   // loyer (Σ brut des décomptes validés)
  amortissement_annuel: number | null;
  valeur_residuelle: number | null;
  cout_revient_horaire: number | null;    // null = non calculable (« — »)
  nb_releves: number;
}

export async function listEngins(params?: { q?: string; statut?: EnginStatut; categorie?: EnginCategorie; chantier_id?: string; archives?: boolean }): Promise<Page<EnginDTO>> {
  const { data } = await api.get<Page<EnginDTO>>('/engins', { params });
  return data;
}
export async function getEngin(id: string): Promise<EnginDTO> {
  const { data } = await api.get<EnginDTO>(`/engins/${id}`);
  return data;
}
export async function immobiliserEngin(id: string): Promise<{ immobilisation_id: string; code: string; duree_mois: number; detail: string }> {
  const { data } = await api.post(`/engins/${id}/immobiliser`);
  return data;
}
export async function createEngin(payload: EnginInput): Promise<EnginDTO> {
  const { data } = await api.post<EnginDTO>('/engins', payload);
  return data;
}
export async function updateEngin(id: string, payload: Partial<EnginInput> & { is_archived?: boolean }): Promise<EnginDTO> {
  const { data } = await api.put<EnginDTO>(`/engins/${id}`, payload);
  return data;
}
export async function deleteEngin(id: string): Promise<void> {
  await api.delete(`/engins/${id}`);
}
export async function getEnginsDashboard(): Promise<EnginsDashboardDTO> {
  const { data } = await api.get<EnginsDashboardDTO>('/engins/dashboard');
  return data;
}

/* -------------------------------- Relevés --------------------------------- */
export async function listReleves(enginId: string): Promise<ReleveDTO[]> {
  const { data } = await api.get<ReleveDTO[]>(`/engins/${enginId}/releves`);
  return data;
}
export async function addReleve(payload: { engin_id: string; chantier_id?: string | null; date: string; heures_marche: number; heures_arret?: number; carburant_litre?: number; prix_unitaire?: number; notes?: string | null }): Promise<ReleveDTO> {
  const { data } = await api.post<ReleveDTO>('/engins/releves', payload);
  return data;
}
export async function updateReleve(releveId: string, payload: { date?: string; heures_marche?: number; heures_arret?: number; chantier_id?: string | null; notes?: string | null }): Promise<ReleveDTO> {
  const { data } = await api.put<ReleveDTO>(`/engins/releves/${releveId}`, payload);
  return data;
}
export async function deleteReleve(releveId: string): Promise<void> {
  await api.delete(`/engins/releves/${releveId}`);
}

/* ------------------------------ Maintenances ------------------------------ */
export async function listMaintenances(enginId: string): Promise<MaintenanceDTO[]> {
  const { data } = await api.get<MaintenanceDTO[]>(`/engins/${enginId}/maintenances`);
  return data;
}
export async function addMaintenance(payload: { engin_id: string; type: MaintenanceType; date_debut?: string | null; description?: string | null; cout_pieces?: number; cout_main_oeuvre?: number; statut?: MaintenanceStatut; compteur?: number | null; plan_id?: string | null; justificatif_url?: string | null }): Promise<void> {
  await api.post('/engins/maintenances', payload);
}
export async function updateMaintenance(maintenanceId: string, payload: { type?: MaintenanceType; date_debut?: string | null; description?: string | null; cout_pieces?: number; cout_main_oeuvre?: number; statut?: MaintenanceStatut; compteur?: number | null; plan_id?: string | null; justificatif_url?: string | null }): Promise<void> {
  await api.put(`/engins/maintenances/${maintenanceId}`, payload);
}
export async function deleteMaintenance(maintenanceId: string): Promise<void> {
  await api.delete(`/engins/maintenances/${maintenanceId}`);
}
export async function cloturerMaintenance(enginId: string, maintenanceId: string): Promise<void> {
  await api.post(`/engins/${enginId}/maintenances/${maintenanceId}/cloturer`);
}

/* --------------- Plans de maintenance préventive (heures/date) ------------- */
export async function listPlans(enginId: string): Promise<PlanDTO[]> {
  const { data } = await api.get<PlanDTO[]>(`/engins/${enginId}/plans`);
  return data;
}
export async function addPlan(enginId: string, payload: PlanInput): Promise<PlanDTO> {
  const { data } = await api.post<PlanDTO>(`/engins/${enginId}/plans`, payload);
  return data;
}
export async function updatePlan(enginId: string, planId: string, payload: Partial<PlanInput>): Promise<PlanDTO> {
  const { data } = await api.put<PlanDTO>(`/engins/${enginId}/plans/${planId}`, payload);
  return data;
}
export async function deletePlan(enginId: string, planId: string): Promise<void> {
  await api.delete(`/engins/${enginId}/plans/${planId}`);
}
export async function listMaintenanceAlertes(): Promise<PlanAlerteDTO[]> {
  const { data } = await api.get<PlanAlerteDTO[]>('/engins/maintenance/alertes');
  return data;
}

/* ------------------------------ Affectations ------------------------------ */
export async function listAffectations(enginId: string): Promise<AffectationDTO[]> {
  const { data } = await api.get<AffectationDTO[]>(`/engins/${enginId}/affectations`);
  return data;
}
export async function addAffectation(enginId: string, payload: { chantier_id: string; date_debut?: string | null; date_fin?: string | null; heures_planifiees?: number | null }): Promise<AffectationDTO> {
  const { data } = await api.post<AffectationDTO>(`/engins/${enginId}/affectations`, payload);
  return data;
}

/* -------------------------------- Synthèse -------------------------------- */
export async function getSyntheseEngin(enginId: string): Promise<SyntheseEnginDTO> {
  const { data } = await api.get<SyntheseEnginDTO>(`/engins/${enginId}/synthese`);
  return data;
}

/* -------------------------------- Carburant ------------------------------- */
export interface PleinCarburantDTO {
  id: string;
  engin_id: string;
  chantier_id: string | null;
  date_plein: string;
  quantite_litres: number;
  prix_unitaire: number;
  montant: number;
  compteur_au_plein: number | null;
  station: string | null;
  reference_bon: string | null;
  justificatif_url: string | null;
  notes: string | null;
  delta_compteur: number | null;
  consommation: number | null;       // L/100 km ou L/h
  unite_consommation: string | null;  // "L/100km" | "L/h"
  surconsommation: boolean;
}
export interface PleinInput {
  chantier_id?: string | null;
  date_plein: string;
  quantite_litres: number;
  prix_unitaire?: number;
  montant?: number | null;
  compteur_au_plein?: number | null;
  station?: string | null;
  reference_bon?: string | null;
  justificatif_url?: string | null;
  notes?: string | null;
}
export interface CarburantSyntheseChantierDTO { chantier_id: string; litres: number; cout: number }
export interface CarburantSyntheseDTO {
  nb_pleins: number;
  total_litres: number;
  total_cout: number;
  conso_moyenne: number | null;
  unite_consommation: string;
  nb_surconsommations: number;
  par_chantier: CarburantSyntheseChantierDTO[];
}
export interface CarburantAlerteDTO extends PleinCarburantDTO {
  engin_code: string;
  engin_designation: string;
  conso_moyenne: number | null;
  ecart_pct: number | null;
}

export async function listPleins(enginId: string): Promise<PleinCarburantDTO[]> {
  const { data } = await api.get<PleinCarburantDTO[]>(`/engins/${enginId}/pleins`);
  return data;
}
export async function addPlein(enginId: string, payload: PleinInput): Promise<PleinCarburantDTO> {
  const { data } = await api.post<PleinCarburantDTO>(`/engins/${enginId}/pleins`, payload);
  return data;
}
export async function updatePlein(enginId: string, pleinId: string, payload: Partial<PleinInput>): Promise<PleinCarburantDTO> {
  const { data } = await api.put<PleinCarburantDTO>(`/engins/${enginId}/pleins/${pleinId}`, payload);
  return data;
}
export async function deletePlein(enginId: string, pleinId: string): Promise<void> {
  await api.delete(`/engins/${enginId}/pleins/${pleinId}`);
}
export async function getCarburantSynthese(enginId: string, mois?: number, annee?: number): Promise<CarburantSyntheseDTO> {
  const { data } = await api.get<CarburantSyntheseDTO>(`/engins/${enginId}/carburant/synthese`, { params: { mois, annee } });
  return data;
}
export async function listCarburantAlertes(): Promise<CarburantAlerteDTO[]> {
  const { data } = await api.get<CarburantAlerteDTO[]>('/engins/carburant/alertes');
  return data;
}

/* ---------------- Échéances (assurance, contrôle technique…) --------------- */
export async function listEcheances(enginId: string): Promise<EcheanceDTO[]> {
  const { data } = await api.get<EcheanceDTO[]>(`/engins/${enginId}/echeances`);
  return data;
}
export async function addEcheance(enginId: string, payload: Omit<EcheanceDTO, 'id' | 'engin_id'>): Promise<EcheanceDTO> {
  const { data } = await api.post<EcheanceDTO>(`/engins/${enginId}/echeances`, payload);
  return data;
}
export async function updateEcheance(enginId: string, id: string, payload: Partial<EcheanceDTO>): Promise<EcheanceDTO> {
  const { data } = await api.put<EcheanceDTO>(`/engins/${enginId}/echeances/${id}`, payload);
  return data;
}
export async function deleteEcheance(enginId: string, id: string): Promise<void> {
  await api.delete(`/engins/${enginId}/echeances/${id}`);
}
export async function listEcheancesAvenir(jours = 60): Promise<EcheanceAvenirDTO[]> {
  const { data } = await api.get<EcheanceAvenirDTO[]>('/engins/echeances/a-venir', { params: { jours } });
  return data;
}
